// useReferralCapture · captura del código de referido por URL (?ref=CODE).
//
// Comportamiento:
//   1. Al montar (cualquier estado de auth) lee ?ref=CODE de la URL. Si viene,
//      lo guarda en localStorage (clave robotea_ref) y limpia el query param
//      de la URL para que no quede compartible.
//   2. Cuando el usuario pasa a autenticado, hace UNA SOLA vez POST a
//      /api/me/referral/attribute con el código pendiente. En éxito borra la
//      clave de localStorage. Fire-and-forget: si falla, se ignora (el código
//      queda en localStorage para reintentar en una sesión futura).
//
// Se monta una vez en App (dentro de AuthProvider). Owner: Carlos Beltrán · 2026-06-09.

import { useEffect, useRef } from 'react';
import { useAuth } from './auth-context';
import { attributeReferral, REFERRAL_STORAGE_KEY } from './referral-api';

function readStoredRef(): string | null {
  try {
    return window.localStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeRef(code: string): void {
  try {
    window.localStorage.setItem(REFERRAL_STORAGE_KEY, code);
  } catch {
    /* localStorage no disponible · ignorar */
  }
}

function clearStoredRef(): void {
  try {
    window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function useReferralCapture(): void {
  const { state } = useAuth();
  // Evita disparar el atributo más de una vez por sesión de montaje.
  const attributedRef = useRef(false);

  // 1) Capturar ?ref=CODE de la URL al montar.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref');
    if (!code) return;
    const trimmed = code.trim();
    if (trimmed) storeRef(trimmed);
    // Limpiar el param de la URL para que no quede compartible.
    const url = new URL(window.location.href);
    url.searchParams.delete('ref');
    window.history.replaceState({}, '', url.toString());
  }, []);

  // 2) Atribuir el código pendiente cuando el usuario está autenticado.
  useEffect(() => {
    if (state.status !== 'authenticated' || !state.identity) return;
    if (attributedRef.current) return;
    const code = readStoredRef();
    if (!code) return;
    attributedRef.current = true;
    void (async () => {
      const ok = await attributeReferral(state.identity, code);
      if (ok) clearStoredRef();
    })();
  }, [state]);
}
